
import { PartCoverageEstimatorStencil } from "./partCoverageStencil";

let partCoverageEstimator = new PartCoverageEstimatorStencil();

const regionPartMapping = {
  Front: new Set(["front_bumper", "front_glass"]),
  Rear: new Set(["back_bumper", "back_glass"]),
  "Side-Left": new Set(["left_back_door", "left_front_door", "left_window_glass"]),
  "Side-Right": new Set(["right_back_door", "right_front_door", "right_window_glass"]),
  "Front-Isometric-Left": new Set(["left_fender"]),
  "Front-Isometric-Right": new Set(["right_fender"]),
  "Rear-Isometric-Left": new Set(["left_qtr_panel"]),
  "Rear-Isometric-Right": new Set(["right_qtr_panel"]),
  "Side-Left-Front": new Set(["back_door", "window_glass"]),
  "Side-Left-Back": new Set(["front_door", "window_glass"]),
  "Side-Right-Front": new Set(["back_door", "window_glass"]),
  "Side-Right-Back": new Set(["front_door", "window_glass"]),
};

const regionPartMappingFull = {
  Front: new Set(["front_bumper", "hood", "front_glass"]), 
  Rear: new Set(["back_bumper", "dicky", "back_glass"]), 
  "Side-Left": new Set(["left_front_door", "left_back_door", "left_window_glass"]),
  "Side-Right": new Set(["right_front_door", "right_back_door", "right_window_glass"]),
  "Front-Isometric-Left": new Set(["left_fender"]),
  "Front-Isometric-Right": new Set(["right_fender"]),
  "Rear-Isometric-Left": new Set(["left_qtr_panel"]),
  "Rear-Isometric-Right": new Set(["right_qtr_panel"]),
  "Side-Left-Front": new Set(["back_door", "window_glass"]),
  "Side-Left-Back": new Set(["front_door", "window_glass"]),
  "Side-Right-Front": new Set(["back_door", "window_glass"]),
  "Side-Right-Back": new Set(["front_door", "window_glass"]),
};
const zoomedInRegionList = ["Side-Left-Front", "Side-Right-Front", "Side-Right-Back", "Side-Left-Back"]

const minimumCoveredAreaThreshold = {
  front_bumper: 0.07,
  front_glass: 0.04,
  hood: 0.04,
  dicky: 0.035,
  back_bumper: 0.065,
  back_glass: 0.04,
  left_back_door: 0.04,
  left_front_door: 0.04,
  right_back_door: 0.04,
  right_front_door: 0.04,
};

const angleFlag = (currentRegion, currentAngle) => {
  if (currentRegion === "Side-Right-Back") {
    return currentAngle >= 110 && currentAngle <= 160;
  }

  if (currentRegion === "Side-Right-Front") {
    return currentAngle >= 20 && currentAngle <= 70;
  }

  if (currentRegion === "Side-Left-Back") {
    return currentAngle >= 200 && currentAngle <= 250;
  }

  if (currentRegion === "Side-Left-Front") {
    return currentAngle >= 290 && currentAngle <= 340;
  }

  return false;
};

const hasValidIntersection = (partList, region) =>{
  if (!regionPartMappingFull[region]) {
    return false; // invalid region
  }

  const regionParts = regionPartMappingFull[region];

  // Count intersection
  let intersectionCount = 0;
  for (const part of partList) {
    if (regionParts.has(part)) {
      intersectionCount++;
    }
  }

  // Regions that require at least 2 matching parts
  const requireTwoParts = ["Front", "Rear", "Side-Left", "Side-Right"];

  if (requireTwoParts.includes(region)) {
    return intersectionCount >= 2;
  } else {
    return intersectionCount >= 1;
  }
}

const check_proper_area_covered = (currentRegion, coveredPart, areaData) => {
  const partsInRegion = regionPartMapping[currentRegion] || new Set();
//   console.log(partsInRegion)
  // Convert Set → Array and find intersection
  const actualPartInRegion = [...partsInRegion].filter(part =>
    coveredPart.includes(part)
  );
//   console.log(actualPartInRegion)
  for (let part of actualPartInRegion) {
    if (
      areaData[part] !== undefined &&
      minimumCoveredAreaThreshold[part] !== undefined &&
      areaData[part] < minimumCoveredAreaThreshold[part]
    ) {
      return false;
    }
  }
  return true;
}
/**
 * Check if all required parts for a region are covered
 */
const checkCoveredPartRegion = (coveredPartList, currentRegion) => {
  if (!(currentRegion in regionPartMapping)) return 5;

  const actualCoveredPartList = regionPartMapping[currentRegion];
  const coveredSet = new Set(coveredPartList);

  for (let part of actualCoveredPartList) {
    if (!coveredSet.has(part)) {
      return 5;
    }
  }
  return 0;
};

/**
 * Calculate distance between two points along X or Y axis
 */
const calculateDistance = (p1, p2, axis) => {
  axis = axis.toUpperCase();
  if (axis === "X") return Math.abs(p2[0] - p1[0]);
  if (axis === "Y") return Math.abs(p2[1] - p1[1]);
  throw new Error("Axis must be 'X' or 'Y'");
};

/**
 * Keep only largest bbox for each label except some exceptions
 */
const filterByLargerBbox = (detections) => {
  const bestDetections = {};
  const keepAll = ["wheel", "window_glass", "headlight", "taillight"];

  detections.forEach((det) => {
    const { label, bbox } = det;
    const [x, y, w, h] = bbox;
    const area = w * h;

    if (keepAll.includes(label)) {
      if (!(label in bestDetections)) bestDetections[label] = [];
      bestDetections[label].push({ area, det });
    } else {
      if (!(label in bestDetections) || area > bestDetections[label].area) {
        bestDetections[label] = { area, det };
      }
    }
  });

  return Object.values(bestDetections).flatMap((entry) =>
    Array.isArray(entry) ? entry.map((e) => e.det) : entry.det
  );
};

/**
 * Check whether point b lies between a and c on given axis
 */
const isBetween = (a, b, c, axis) => {
  axis = axis.toUpperCase();
  if (axis === "X") return Math.min(a[0], c[0]) < b[0] && b[0] < Math.max(a[0], c[0]);
  if (axis === "Y") return Math.min(a[1], c[1]) < b[1] && b[1] < Math.max(a[1], c[1]);
  throw new Error("Axis must be 'X' or 'Y'");
};

/**
 * Check bounding box alignment with stencil lines along given axis
 */
const checkAxis = (stencil1, stencil2, mask1, mask2, axis) => {
  const p1Between = isBetween(stencil1, mask1, stencil2, axis);
  const p2Between = isBetween(stencil1, mask2, stencil2, axis);

  const stencilDistance = calculateDistance(stencil1, stencil2, axis);
  const maskDistance = calculateDistance(mask1, mask2, axis);

  return [p1Between, p2Between, stencilDistance, maskDistance];
};

/**
 * Calculate bounding box extremes from detections
 */
const minMaxOfMask = (detections, currentRegion, frameResolution) => {
  let xmin = Infinity, ymin = Infinity;
  let xmax = -Infinity, ymax = -Infinity;
  let addedPartFlag = false

  const allPart = detections.map(data => data.label);

  // Adjustment for Rear region if back_glass is missing
  if (currentRegion === "Rear" && !allPart.includes("back_glass") && allPart.includes("dicky")) {
    detections.forEach(data => {
      if (data.label === "dicky" && data.bbox[1] > 0.27) {
        data.bbox[1] -= 0.2;
        data.bbox[3] += 0.2;
        addedPartFlag = true
        
      }
    });
  }
  // Adjustment for Rear region if back_glass and tail gate both are missing
  if (currentRegion === "Rear" && !allPart.includes("back_glass") && !allPart.includes("dicky") && allPart.includes("back_bumper")) {
    detections.forEach(data => {
      if (data.label === "back_bumper" && data.bbox[1] > 0.5) {
        data.bbox[1] -= 0.4;
        data.bbox[3] += 0.4;
        addedPartFlag = true
      }
    });
  }
  // Adjustment for Front region if front_glass is missing
  if (currentRegion === "Front" && !allPart.includes("front_glass") && allPart.includes("hood")) {
    detections.forEach(data => {
      if (data.label === "hood" && data.bbox[1] > 0.27) {
        data.bbox[1] -= 0.2;
        data.bbox[3] += 0.2;
        addedPartFlag = true
      }
    });
  }
  // Adjustment for Front region if front_glass and hood both are missing
  if (currentRegion === "Front" && !allPart.includes("front_glass") && !allPart.includes("hood") && allPart.includes("front_bumper")) {
    detections.forEach(data => {
      if (data.label === "front_bumper" && data.bbox[1] > 0.5) {
        data.bbox[1] -= 0.4;
        data.bbox[3] += 0.4;
        addedPartFlag = true
      }
    });
  }

  const actualCoveredPartList = regionPartMapping[currentRegion];
  // Only the zoomed-in sub-stencils narrow the mask to their own parts, and only
  // they can: their regionPartMapping entries use the detector's raw labels
  // ("back_door", "window_glass"), which is what det.label holds here.
  // Side-Left/Side-Right map to side-prefixed names ("left_back_door", ...) that
  // never match a raw label, so filtering on them matched nothing and left the
  // mask at +/-Infinity — pinning the flag at 1 so the side stencil could go blue
  // but never green. Full-side regions keep the whole-detection mask.
  if (zoomedInRegionList.includes(currentRegion)){
    detections.forEach(det => {
      if (actualCoveredPartList.has(det.label)){
        const [x1, y1, w, h] = det.bbox;
        const x2 = x1 + w;
        const y2 = y1 + h;
    
        xmin = Math.min(xmin, x1);
        ymin = Math.min(ymin, y1);
        xmax = Math.max(xmax, x2);
        ymax = Math.max(ymax, y2);
      }
      })

  }
  else {
  detections.forEach(det => {
    const [x1, y1, w, h] = det.bbox;
    const x2 = x1 + w;
    const y2 = y1 + h;

    xmin = Math.min(xmin, x1);
    ymin = Math.min(ymin, y1);
    xmax = Math.max(xmax, x2);
    ymax = Math.max(ymax, y2);
  });
  }

  return [
    [xmin * frameResolution - 4, ymin * frameResolution - 4],
    [xmax * frameResolution + 4, ymax * frameResolution + 4],
    addedPartFlag,
  ];
};

/**
 * Stub for checking up/down movement
 */
const upDownFlag = (detection) => {
  return 0;
};
const getAreaData = (detection) =>{
  const areaData = {};

  detection.forEach(item => {
    const area = item.bbox[2] * item.bbox[3];

    if (!areaData[item.label]) {
      areaData[item.label] = area;
    } else {
      if (areaData[item.label] < area) {
        areaData[item.label] = area;
      }
    }
  });

  return areaData;
}

/**
 * Main function to evaluate good frame
 */


export const goodFrame = (
  stencilMin,
  stencilMax,
  raw_detections,
  currentRegion,
  currentAngle,
  frameResolution,
  subStencil = ""
) => {
  let flag = -1;
  let getAngleFlag = false
  
  const detection = filterByLargerBbox(raw_detections);


  const areaData = getAreaData(detection)
  const [maskMin, maskMax, addedPartFlag] = minMaxOfMask(detection, currentRegion, frameResolution);
  const partList = detection.map(item => item.label);
  
  if (!detection || detection.length === 0) {
    return { flagValue: flag, partialDetection: false, maskMin, maskMax,  part_list: { flagValue: flag,
      partList: partList,
      msg:"not detection",
      areaData,
      currentRegion,
      getAngleFlag: getAngleFlag

    } };
  }

  // let coveredPart = partCoverageEstimator.getPartsCovered(detection, currentAngle);
  // if (addedPartFlag){
  //   if (currentRegion == "Front"){
  //     coveredPart.push("front_glass")
  //   }
  //   else if (currentRegion == "Rear"){
  //     coveredPart.push("back_glass")
  //   }
  // }
  let coveredPart = []
  if (zoomedInRegionList.includes(currentRegion)){
    coveredPart = partList
  }
  else {
    coveredPart = partCoverageEstimator.getPartsCovered(detection, currentAngle);
    if (addedPartFlag){
      if (currentRegion == "Front"){
        coveredPart.push("front_glass")
      }
      else if (currentRegion == "Rear"){
        coveredPart.push("back_glass")
      }
    }
  }
  const properAreaCovered = check_proper_area_covered(currentRegion, coveredPart, areaData)
  getAngleFlag = angleFlag(currentRegion, currentAngle)
  if (!properAreaCovered){
    return { flagValue: 2, maskMin, maskMax,  part_list: { flagValue: 2,
      partList: partList,
      coveredPart: coveredPart,
      msg:"Too faaar",
      areaData,
      currentRegion,
      getAngleFlag: getAngleFlag
    } }
  }
  const [p1BetweenX, p2BetweenX, stencilDistanceX, maskDistanceX] =
    checkAxis(stencilMin, stencilMax, maskMin, maskMax, "X");
  const [p1BetweenY, p2BetweenY, stencilDistanceY, maskDistanceY] =
    checkAxis(stencilMin, stencilMax, maskMin, maskMax, "Y");

  // If region parts are not covered properly
  const updated_flag = checkCoveredPartRegion(coveredPart, currentRegion)

  const partialDetectionFlag = hasValidIntersection(coveredPart, currentRegion)

  // Side regions (Left/Right)
  if (currentRegion === "Side-Left" || currentRegion === "Side-Right") {
    if ((stencilMin[0]-maskMin[0])>-22 && (stencilMin[0]-maskMin[0])<7 && (stencilMax[0]-maskMax[0])>-7 && (stencilMax[0]-maskMax[0])<22){
      flag = updated_flag;
    }
    else if (p1BetweenX && p2BetweenX) {
      if (
        0.75 * stencilDistanceX <= maskDistanceX &&
        maskDistanceX <= stencilDistanceX &&
        0.6 * stencilDistanceY <= maskDistanceY
      ) {
        flag = updated_flag;
      } else {
        flag = 2;
      }
    } else if (!p1BetweenX && !p2BetweenX) {
      if (
        stencilDistanceX <= maskDistanceX &&
        maskDistanceX <= 1.1 * stencilDistanceX &&
        0.6 * stencilDistanceY <= maskDistanceY &&
        maskDistanceY <= 1.2 * stencilDistanceY
      ) {
        flag = updated_flag;
      } else {
        flag = 1;
      }
    }
  } 
  else if (zoomedInRegionList.includes(currentRegion)){

    if (getAngleFlag){
      flag = updated_flag
    }
    else {
      flag = 6
    }
  } 
  // Other regions
  else {
    if ((stencilMin[1]-maskMin[1])>-20 && (stencilMin[1]-maskMin[1])<6 && (stencilMax[1]-maskMax[1])>-6 && (stencilMax[1]-maskMax[1])<20){
      flag = updated_flag;
    }
    else if (p1BetweenY && p2BetweenY) {
      if (
        0.75 * stencilDistanceY <= maskDistanceY &&
        maskDistanceY <= stencilDistanceY &&
        0.5 * stencilDistanceX <= maskDistanceX
      ) {
        flag = updated_flag;
      } else {
        flag = 2;
      }
    } else if (!p1BetweenY && !p2BetweenY) {
      if (
        stencilDistanceY <= maskDistanceY &&
        maskDistanceY <= 1.4 * stencilDistanceY &&
        0.5 * stencilDistanceX <= maskDistanceX &&
        maskDistanceX <= 1.1 * stencilDistanceX
      ) {
        flag = updated_flag;
      } else {
        flag = 1;
      }
    }
  }

  return {
    flagValue: flag,
    partialDetection: partialDetectionFlag,
    maskMin,
    maskMax,
    part_list: {
      flagValue: flag,
      dataX: [p1BetweenX, p2BetweenX, stencilDistanceX, maskDistanceX],
      dataY: [p1BetweenY, p2BetweenY, stencilDistanceY, maskDistanceY],
      partList: partList,
      covered: coveredPart,
      msg:"stencil",
      currentAngle: currentAngle,
      areaData,
      currentRegion,
      getAngleFlag: getAngleFlag
    }
  };
};

// flag values meaning
// -1 = default
// 0 = goodFrame
// 1 = move away from car
// 2 = move towards car
// 3 = move upwards
// 4 = move downwards
// 5 = Please adjust your camera to align with the stencils
